import { DEFAULT_SAVED_ROW_CATEGORY_HANDLES, labelForSavedRowCategoryHandle } from 'utils/savedEmailRows';

import type { IPluginRow } from '@beefree.io/sdk/dist/types/bee';
import type { BeeConfig, BeeEmailEditorConfig } from 'domains/content/types';

/** Bee `getRows` needs a non-empty primary handle when the tenant has no categories yet. */
const DEFAULT_SAVED_ROWS_BEE_PRIMARY_HANDLE = DEFAULT_SAVED_ROW_CATEGORY_HANDLES[0];

export type BuildEmailBeeSavedRowsConfigParams = {
    base: BeeConfig;
    fetchRowsForCategory: (handle: string) => Promise<IPluginRow[]>;
    promptSaveRow: (row: IPluginRow) => Promise<Record<string, unknown>>;
    /** Extra category handles (API + defaults) so Bee’s Rows tab registers each `getRows` source. */
    extraCategoryHandles?: string[];
};

/**
 * Merges Beefree self-hosted saved rows (Phase 1) into the email token config from `useBeeConfig`.
 *
 * Maps Beefree tutorial `ClientConfig` → our merge (container / language / `uid` stay on `BeeEditor` defaults + `beeConfig.client_uid`):
 * - `saveRows: true`
 * - `hooks.getRows` → `fetchRowsForCategory` (args.handle / args.value from Beefree)
 * - `rowsConfiguration.emptyRows` → `true` so dashed row placeholders always show in the email editor.
 * - `rowsConfiguration.defaultRows` → `true` with self-hosted saved rows so Bee’s default row strip is available.
 * - `rowsConfiguration.externalContentURLs` → first API category (or a default slug), then remaining handles, then upstream entries
 * - `contentDialog.saveRow` → `promptSaveRow` (name/category modal + POST)
 *
 * @see docs/spec-bee-self-hosted-saved-rows.md
 */
export const buildEmailBeeSavedRowsConfig = ({
    base,
    fetchRowsForCategory,
    promptSaveRow,
    extraCategoryHandles = [],
}: BuildEmailBeeSavedRowsConfigParams): BeeEmailEditorConfig => {
    const primarySlug = extraCategoryHandles[0] ?? DEFAULT_SAVED_ROWS_BEE_PRIMARY_HANDLE;
    const primaryCategory = {
        value: primarySlug,
        label: labelForSavedRowCategoryHandle(primarySlug),
    };
    const upstreamContentDialog = (base as Partial<BeeEmailEditorConfig>).contentDialog;
    const upstreamHooks = (base as Partial<BeeEmailEditorConfig>).hooks ?? {};
    const upstreamRowsConfiguration = (base as Partial<BeeEmailEditorConfig>).rowsConfiguration ?? {};

    // `isLocal: true` + non-empty `value` match Beefree’s getRows tutorial: otherwise `value: ''` is treated like a
    // fetch URL and the Rows panel / post-save refresh can hang waiting on a bad request.
    const savedRowsCategoryEntry = {
        name: primaryCategory.label,
        value: primaryCategory.value,
        handle: primaryCategory.value,
        isLocal: true as const,
        behaviors: { canEdit: false as const, canDelete: false as const },
    };

    const upstreamEntries = upstreamRowsConfiguration.externalContentURLs ?? [];
    const upstreamHandles = new Set(
        upstreamEntries.map((e) => e?.handle).filter((h): h is string => typeof h === 'string' && h.length > 0)
    );

    const uniqueExtra = Array.from(new Set(extraCategoryHandles)).filter(
        (h) => h && h !== primaryCategory.value && !upstreamHandles.has(h)
    );

    const extraEntries = uniqueExtra.map((handle) => ({
        name: labelForSavedRowCategoryHandle(handle),
        value: handle,
        handle,
        isLocal: true as const,
        behaviors: { canEdit: false as const, canDelete: false as const },
    }));

    const otherExternalUrls = upstreamEntries.filter((entry) => entry?.handle !== primaryCategory.value);
    const externalContentURLs = [savedRowsCategoryEntry, ...extraEntries, ...otherExternalUrls];

    return {
        ...base,
        // Avoid Bee’s full-screen spinner over our save-row modal (see SaveRowDialog).
        loadingSpinnerDisableOnDialog: true,
        loadingSpinnerDisableOnSave: true,
        saveRows: true,
        rowsConfiguration: {
            ...upstreamRowsConfiguration,
            emptyRows: true,
            defaultRows: true,
            externalContentURLs,
        },
        hooks: {
            ...upstreamHooks,
            getRows: {
                handler: async (
                    resolve: (rows: IPluginRow[]) => void,
                    reject: () => void,
                    args: { handle: string; value: string }
                ) => {
                    try {
                        const rows = await fetchRowsForCategory(args.handle);
                        resolve(rows);
                    } catch {
                        reject();
                    }
                },
            },
        },
        contentDialog: {
            ...(upstreamContentDialog ?? {}),
            saveRow: {
                label: 'Save row',
                // Beefree saved-rows docs: `resolve(metadata, { synced })` — omitting the options object can fail
                // validation and leave the editor in waiting mode with a perpetual loading overlay.
                handler: async (
                    resolve: (data: Record<string, unknown>, options?: Record<string, unknown>) => void,
                    reject: () => void,
                    row: IPluginRow
                ) => {
                    try {
                        const payload = await promptSaveRow(row);
                        // Phase 1: standard rows only; `template_rows` + row metadata carry library linkage (not Beefree synced-rows).
                        resolve(payload, { synced: false });
                    } catch {
                        reject();
                    }
                },
            },
        },
    };
};
